import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import {
  Pagination as ShadcnPagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import Skeleton from './Skeleton';

const Pagination = ({ currentPage, isLoading, onPageChange, totalPages }) => {
  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const pageNumbers = useMemo(() => {
    // Show all pages if there are three or fewer.
    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // If there are more than three pages, include the first, last, and current pages.
    // Additionally, include the pages before and after the current page.
    const pages = new Set([
      1,
      currentPage,
      totalPages,
      Math.max(1, currentPage - 1),
      Math.min(totalPages, currentPage + 1),
    ]);

    // Convert the set to a sorted array.
    return Array.from(pages).sort((a, b) => a - b);
  }, [currentPage, totalPages]);

  if (isLoading) {
    return <Skeleton />;
  }

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  return (
    <ShadcnPagination className="mx-0 w-auto">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious disabled={isFirstPage} onClick={handlePrevious} />
        </PaginationItem>

        {pageNumbers.map((page, index) => {
          // Show an ellipsis between the current item and the next item if there is a gap
          // between the two.
          const isLastItem = index === pageNumbers.length - 1;
          const nextItem = pageNumbers[index + 1];
          const showEllipsis = !isLastItem && nextItem - page > 1;

          return (
            <React.Fragment key={page}>
              <PaginationItem>
                <PaginationLink
                  isActive={currentPage === page}
                  onClick={() => onPageChange(page)}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>

              {showEllipsis && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}
            </React.Fragment>
          );
        })}

        <PaginationItem>
          <PaginationNext disabled={isLastPage} onClick={handleNext} />
        </PaginationItem>
      </PaginationContent>
    </ShadcnPagination>
  );
};

Pagination.Skeleton = Skeleton;

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onPageChange: PropTypes.func.isRequired,
  totalPages: PropTypes.number.isRequired,
};

export default Pagination;
